import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Link,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useCallback, useEffect, useState } from 'react';
import i18n from '../../i18n';
import { fDateTime } from '../../utils/formatTime';
import { formatApiErrors } from '../../utils/formatError';
import { PAYOUT_DOCUMENT_SOURCE } from '../../constants/payoutDocumentSource';

/**
 * A payout's receipts — the bank's proof its money was sent — opened from its row.
 *
 * Read-only: core attaches them, from a reply to the tg gateway bot or with the
 * cabinet's manual confirm, and nothing edits or deletes one. Each opens through
 * its own token route, scoped to the payouts the user can see, rather than
 * /api/files/token, which gateway users are denied.
 */
export const PayoutReceiptsDialog = ({ payout, openDialog, closeDialog, server, files }) => {
    const [rows, setRows] = useState(null);
    const [error, setError] = useState(null);
    const [opening, setOpening] = useState(null);

    const fetchData = useCallback(async () => {
        if (!payout?.id) return;
        setError(null);
        const response = await server.byPayout(payout.id);
        if (response.error === 'OK') setRows(response.data || []);
        else setError(formatApiErrors(response).join(' '));
    }, [payout?.id, server]);

    useEffect(() => {
        if (openDialog) fetchData();
    }, [openDialog, fetchData]);

    const close = () => {
        setRows(null);
        setError(null);
        setOpening(null);
        closeDialog();
    };

    const open = async (row) => {
        setOpening(row.id);
        setError(null);
        try {
            const response = await server.token(row.id);
            if (response.error === 'OK' && response.data) files.openByTokenInNewTab(response.data);
            else setError(formatApiErrors(response).join(' '));
        } finally {
            setOpening(null);
        }
    };

    return (
        <Dialog open={Boolean(openDialog)} onClose={close} fullWidth maxWidth="md">
            <DialogTitle>
                {i18n.t('dialogs.payoutReceipts')}
                {payout?.uuid ? ` — ${payout.uuid}` : ''}
            </DialogTitle>

            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {rows === null && !error && (
                    <Stack spacing={1}>
                        <Skeleton height={32} />
                        <Skeleton height={32} />
                    </Stack>
                )}

                {rows !== null && rows.length === 0 && (
                    <Alert severity="info">{i18n.t('text.payoutReceiptsEmpty')}</Alert>
                )}

                {rows !== null && rows.length > 0 && (
                    // Scrolls inside the dialog rather than widening it: the
                    // comment is free-form.
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{i18n.t('columns.file')}</TableCell>
                                    <TableCell>{i18n.t('columns.source')}</TableCell>
                                    <TableCell>{i18n.t('columns.addedBy')}</TableCell>
                                    <TableCell>{i18n.t('columns.comment')}</TableCell>
                                    <TableCell>{i18n.t('columns.dateCreated')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((row) => (
                                    <TableRow key={row.id} hover>
                                        <TableCell>
                                            <Link
                                                component="button"
                                                underline="hover"
                                                disabled={opening === row.id}
                                                onClick={() => open(row)}
                                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                            >
                                                {row.file?.name || `#${row.id}`}
                                                <OpenInNewIcon fontSize="inherit" />
                                            </Link>
                                            {row.file?.type && (
                                                <Typography variant="caption" component="div" color="text.secondary">
                                                    {row.file.type}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {/* A source added after this build shows as it is,
                                                not as a raw translation key. */}
                                            {PAYOUT_DOCUMENT_SOURCE.includes(row.source)
                                                ? i18n.t(`enums.${row.source}`)
                                                : row.source || '—'}
                                        </TableCell>
                                        <TableCell>{row.addedBy || '—'}</TableCell>
                                        <TableCell
                                            sx={{
                                                maxWidth: 240,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                            title={row.comment || ''}
                                        >
                                            {row.comment || '—'}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            {row.dateCreated ? fDateTime(row.dateCreated, 'd MMM yyyy HH:mm') : '—'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                {/* A receipt sent to the bot arrives while the dialog is open. */}
                <Button startIcon={<RefreshIcon />} onClick={fetchData}>
                    {i18n.t('common.refresh')}
                </Button>
                <Button color="inherit" onClick={close}>{i18n.t('common.close')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default PayoutReceiptsDialog;
